import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { isSameDay } from 'date-fns';

import { ViewportMode } from '@blaze-shared/elements-domain';

import { CalendarDateAdapter } from './calendar-date-adapter';
import { CalendarDay } from './calendar-day.interface';

@Component({
  selector: 'blaze-calendar-dates-view',
  templateUrl: './calendar-dates-view.component.html',
  styleUrls: ['./calendar-dates-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalendarDatesViewComponent implements OnChanges {
  private calendarDateAdapter = inject(CalendarDateAdapter, { optional: true });
  private cdr = inject(ChangeDetectorRef);

  selectedChange = output<Date>();

  @Input() label = '';
  @Input() selected = new Date();
  @Input() hideWeekends = false;
  @Input() firstDayOfWeek: CalendarDay = CalendarDay.Monday;
  @Input() datesWithEvents: Date[] | null = [];
  @Input() viewportMode: ViewportMode | undefined;

  @ViewChild(MatCalendar) calendarComponent: MatCalendar<Date> | undefined;

  dateClass = (date: Date): string => {
    return this.datesWithEvents?.some((d) => isSameDay(d, date)) ? 'calendar-day-with-events' : '';
  };

  dateFilter = (date: Date): boolean => {
    return this.hideWeekends
      ? ![CalendarDay.Saturday, CalendarDay.Sunday].includes(date.getDay())
      : true;
  };

  @HostBinding('class.mobile') get isMobile(): boolean {
    return this.viewportMode === 'mobile';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hideWeekends'] || changes['datesWithEvents']) {
      this.refreshCalendarsSettings();
    }

    if (changes['firstDayOfWeek'] && this.calendarDateAdapter) {
      this.calendarDateAdapter.setFirstDayOfWeek(this.firstDayOfWeek);
      this.refreshCalendarsSettings();
    }
  }

  openYearSelectionView(): void {
    if (!this.calendarComponent) {
      return;
    }
    this.calendarComponent.currentView = 'multi-year';
    this.cdr.markForCheck();
  }

  onSelectedChange(date: Date | null): void {
    if (date) {
      this.selected = date;
      this.selectedChange.emit(date);
    }
  }

  focusCell(date: Date): void {
    if (!this.calendarComponent) {
      return;
    }

    this.calendarComponent.activeDate = date;
    this.calendarComponent.focusActiveCell();

    this.cdr.markForCheck();
  }

  private refreshCalendarsSettings(): void {
    this.calendarComponent?.updateTodaysDate();
  }
}
